import React, { useEffect, useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import axios from "axios";
import { v4 as uuidv4 } from "uuid";
import { Toaster } from "react-hot-toast";
import showToast from "../../../utils/toast";
import BoardCascadingDropdowns from "./BoardCascadingDropdowns";
import {
  fetchQuestionTypes,
  fetchDifficultyLevels,
  resetQuestionForm,
  toggleStatus,
} from "../redux/boardQuestionSlice";
import { BoardMedium_URL } from "../../config";
import Popup from "../../../Popup";

const difficultyMapping = {
  easy: 1,
  medium: 2,
  hard: 3,
};

const UploadQuestionsTab = ({ onUploadSuccess, onClose, onSuccess }) => {
  const dispatch = useDispatch();
  const { boardId } = useParams();

  const userStr = localStorage.getItem("user");
  const userId = userStr ? JSON.parse(userStr).id : null;

  const {
    selectedMedium,
    selectedClass,
    selectedSubject,
    selectedChapter,
    questionTypes,
    difficultyLevels,
    disabledValue,
  } = useSelector((state) => state.boardQuestion);

  const [loadingUpload, setLoadingUpload] = useState(false);
  const [uploadedData, setUploadedData] = useState(null);
  const [editQuestionData, setEditQuestionData] = useState(null);
  const [isEditing, setIsEditing] = useState(false);

  useEffect(() => {
    if (boardId) {
      dispatch(fetchQuestionTypes());
      dispatch(fetchDifficultyLevels());
    }
  }, [boardId, dispatch]);

  const typeMap = useMemo(() => {
    const map = {};
    questionTypes.forEach((item) => {
      map[item.type_id] = item.type_name;
    });
    return map;
  }, [questionTypes]);

  const questionTypeMapping = useMemo(() => {
    const map = {};
    questionTypes.forEach((item) => {
      const name = item.type_name.toLowerCase().trim();
      map[name] = item.type_id;
    });
    return map;
  }, [questionTypes]);

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append("file", file);

    try {
      const response = await axios.post(
        "https://web1024.docmaster.in:5007/api/upload",
        formData,
      );
      let data = response.data;

      dispatch(toggleStatus(!disabledValue));
      if (data && Array.isArray(data.questions)) {
        data.questions = data.questions.map((q) => ({
          ...q,
          id: q.id || uuidv4(),
        }));
      }

      setUploadedData(data);
      setIsEditing(false);
      setEditQuestionData(null);
      showToast.success("File uploaded successfully!");
    } catch (error) {
      console.error("File upload failed:", error);
      showToast.error(
        `File upload failed: ${error.message || "Unknown error"}`,
      );
    }
  };

  const handleUpload = async () => {
    if (!userId || !uploadedData || !uploadedData.questions?.length) {
      showToast.error("Missing user ID or uploaded questions.");
      return;
    }

    if (!selectedClass || !selectedSubject || !selectedChapter) {
      showToast.error(
        "Please select Class, Subject, and Chapter before uploading.",
      );
      return;
    }

    setLoadingUpload(true);
    const toastId = showToast.loading("Uploading questions...");

    const questionsToUpload = uploadedData.questions.map((q) => ({
      ...q,
      question_typeid:
        questionTypeMapping[q.question_type?.toLowerCase()?.trim()] ||
        q.question_typeid ||
        null,
      board_id: parseInt(boardId, 10),
      medium_id: parseInt(selectedMedium, 10),
      class_id: parseInt(selectedClass, 10),
      subject_id: parseInt(selectedSubject, 10),
      chapter_id: parseInt(selectedChapter, 10),
      created_by: userId,
    }));

    const payload = {
      board_id: parseInt(boardId, 10),
      medium_id: parseInt(selectedMedium, 10),
      created_by: userId,
      class_id: parseInt(selectedClass, 10),
      subject_id: parseInt(selectedSubject, 10),
      chapter_id: parseInt(selectedChapter, 10),
      questions: questionsToUpload,
    };

    try {
      await axios.post(`${BoardMedium_URL}/bulk-upload-v3`, payload);
      showToast.dismiss(toastId);
      showToast.success("Questions uploaded successfully!");
      setUploadedData(null);
      dispatch(resetQuestionForm());
      onClose();
      onSuccess();
      // dispatch(resetQuestionForm());
      dispatch(toggleStatus(!disabledValue));

      if (onUploadSuccess) {
        onUploadSuccess();
      }
    } catch (error) {
      console.error("Upload failed:", error);
      showToast.dismiss(toastId);
      showToast.error(
        `Failed to upload questions: ${error.message || "An error occurred"}`,
      );
    } finally {
      setLoadingUpload(false);
    }
  };

  const handleEditQuestion = (question) => {
    setEditQuestionData(question);
    setIsEditing(true);
  };

  const handleUpdateQuestion = (updatedQuestion) => {
    if (!uploadedData) return;

    const updatedQuestions = uploadedData.questions.map((q) =>
      q.id === updatedQuestion.id ? updatedQuestion : q,
    );
    const newUploadedData = { ...uploadedData, questions: updatedQuestions };

    setUploadedData(newUploadedData);
    setIsEditing(false);
    setEditQuestionData(null);
    showToast.success("Question updated successfully!");
  };

  const handleDeleteQuestion = (questionId) => {
    if (!uploadedData || !uploadedData.questions) return;

    const updatedQuestions = uploadedData.questions.filter(
      (q) => q.id !== questionId,
    );
    const updatedData = { ...uploadedData, questions: updatedQuestions };

    setUploadedData(updatedData);
    showToast.success("Question deleted");
  };

  const EditQuestionForm = ({ question, onUpdate, onCancel }) => {
    const [formData, setFormData] = useState(question);

    const handleChange = (e) => {
      const { name, value } = e.target;
      setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handleOptionChange = (index, e) => {
      const newOptions = [...formData.options];
      newOptions[index].text = e.target.value;
      setFormData((prev) => ({ ...prev, options: newOptions }));
    };

    const handleSubmit = (e) => {
      e.preventDefault();
      onUpdate(formData);
    };

    return (
      <div
        style={{
          marginTop: "20px",
          padding: "20px",
          border: "1px solid #e0e0e0",
          borderRadius: "8px",
          backgroundColor: "#f8f9fa",
        }}
      >
        <h3
          style={{
            fontSize: "16px",
            fontWeight: "600",
            marginBottom: "15px",
            color: "#2c3e50",
          }}
        >
          Edit Question
        </h3>
        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: "15px" }}>
            <label
              style={{
                display: "block",
                marginBottom: "6px",
                fontSize: "13px",
                fontWeight: "500",
                color: "#495057",
              }}
            >
              Question Text:
            </label>
            <textarea
              name="question_text"
              value={formData.question_text || ""}
              onChange={handleChange}
              style={{
                width: "100%",
                padding: "8px 12px",
                border: "1px solid #d1d5db",
                borderRadius: "6px",
                fontSize: "14px",
                minHeight: "80px",
                resize: "vertical",
              }}
            />
          </div>

          {Array.isArray(formData.options) && formData.options.length > 0 && (
            <div style={{ marginBottom: "15px" }}>
              <label
                style={{
                  display: "block",
                  marginBottom: "6px",
                  fontSize: "13px",
                  fontWeight: "500",
                  color: "#495057",
                }}
              >
                Options:
              </label>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: "10px",
                }}
              >
                {formData.options.map((opt, index) => (
                  <input
                    key={index}
                    type="text"
                    value={opt.text || ""}
                    onChange={(e) => handleOptionChange(index, e)}
                    placeholder={`Option ${index + 1}`}
                    style={{
                      padding: "8px 12px",
                      border: "1px solid #d1d5db",
                      borderRadius: "6px",
                      fontSize: "14px",
                    }}
                  />
                ))}
              </div>
            </div>
          )}

          <div style={{ marginBottom: "15px" }}>
            <label
              style={{
                display: "block",
                marginBottom: "6px",
                fontSize: "13px",
                fontWeight: "500",
                color: "#495057",
              }}
            >
              Question Type:
            </label>
            <select
              name="question_typeid"
              value={formData.question_typeid || ""}
              onChange={handleChange}
              style={{
                width: "100%",
                padding: "8px 12px",
                border: "1px solid #d1d5db",
                borderRadius: "6px",
                fontSize: "14px",
                height: "38px",
              }}
            >
              {Object.entries(typeMap).map(([id, name]) => (
                <option key={id} value={id}>
                  {name}
                </option>
              ))}
            </select>
          </div>

          <div style={{ marginBottom: "15px" }}>
            <label
              style={{
                display: "block",
                marginBottom: "6px",
                fontSize: "13px",
                fontWeight: "500",
                color: "#495057",
              }}
            >
              Difficulty:
            </label>
            <select
              name="difficulty_id"
              value={formData.difficulty_id || ""}
              onChange={handleChange}
              style={{
                width: "100%",
                padding: "8px 12px",
                border: "1px solid #d1d5db",
                borderRadius: "6px",
                fontSize: "14px",
                height: "38px",
              }}
            >
              {Object.entries(difficultyMapping).map(([name, id]) => (
                <option key={id} value={id}>
                  {name.charAt(0).toUpperCase() + name.slice(1)}
                </option>
              ))}
            </select>
          </div>

          <div
            style={{ display: "flex", gap: "10px", justifyContent: "flex-end" }}
          >
            <button
              type="button"
              onClick={onCancel}
              style={{
                padding: "9px 24px",
                background: "#95a5a6",
                color: "white",
                border: "none",
                borderRadius: "6px",
                fontSize: "14px",
                fontWeight: "500",
                cursor: "pointer",
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              style={{
                padding: "9px 24px",
                background: "#27ae60",
                color: "white",
                border: "none",
                borderRadius: "6px",
                fontSize: "14px",
                fontWeight: "500",
                cursor: "pointer",
              }}
            >
              Save Changes
            </button>
          </div>
        </form>
      </div>
    );
  };

  const canShowFileUpload =
    selectedMedium && selectedClass && selectedSubject && selectedChapter;

  return (
    <>
      <style>
        {`
          .upload-table {
            width: 100%;
            border-collapse: collapse;
            margin-top: 20px;
            font-size: 13px;
            background: white;
            border-radius: 8px;
            overflow: hidden;
          }

          .upload-table th,
          .upload-table td {
            border: 1px solid #e0e0e0;
            padding: 10px;
            text-align: left;
          }

          .upload-table th {
            background-color: #f8f9fa;
            font-weight: 600;
            color: #2c3e50;
          }

          .upload-table tbody tr:hover {
            background-color: #f8f9fa;
          }

          .btn-upload {
            background: #27ae60;
            color: white;
            border: none;
            padding: 10px 24px;
            border-radius: 6px;
            font-weight: 500;
            cursor: pointer;
            transition: all 0.2s ease;
            font-size: 14px;
          }

          .btn-upload:hover:not(:disabled) {
            background: #229954;
          }

          .btn-upload:disabled {
            opacity: 0.6;
            cursor: not-allowed;
          }
        `}
      </style>
      <Popup
        title="Add New Question"
        onClose={() => {
          // resetForm();
          onClose();
        }}
        maxWidth="950px"
      >
        <Toaster position="top-right" />

        <div className="form-card">
          <div className="form-body">
            <h2
              style={{
                margin: 0,
                fontSize: "24px",
                fontWeight: "600",
                color: "#1f2937",
              }}
            >
              Upload Questions
            </h2>
            <p
              style={{
                margin: "4px 0 0 0",
                fontSize: "14px",
                color: "#6b7280",
              }}
            >
              Upload questions from file (.docx, .xlsx, .csv, .json)
            </p>
          </div>

          {/* Board Cascading Dropdowns */}
          <BoardCascadingDropdowns boardId={boardId} showTitle={true} />

          {disabledValue !== true ? "Disable" : "Enable"}
          {/* File Upload Input - Only show after chapter is selected */}
          {canShowFileUpload && !isEditing && (
            <div
              style={{
                marginBottom: "20px",
                padding: "20px",
                background: "white",
                borderRadius: "8px",
                border: "1px solid #e0e0e0",
              }}
            >
              <label
                style={{
                  display: "block",
                  marginBottom: "8px",
                  fontSize: "14px",
                  fontWeight: "500",
                  color: "#495057",
                }}
              >
                Select File to Upload
              </label>
              <input
                type="file"
                accept=".docx,.xlsx,.csv,.json"
                onChange={handleFileUpload}
                style={{
                  width: "100%",
                  padding: "8px 12px",
                  border: "1px solid #d1d5db",
                  borderRadius: "6px",
                  fontSize: "14px",
                  background: "white",
                }}
              />
              <p
                style={{
                  fontSize: "12px",
                  color: "#6c757d",
                  marginTop: "6px",
                  margin: "6px 0 0 0",
                }}
              >
                Supported formats: .docx, .xlsx, .csv, .json
              </p>
            </div>
          )}

          {/* Edit Question Form */}
          {isEditing && editQuestionData && (
            <EditQuestionForm
              question={editQuestionData}
              onUpdate={handleUpdateQuestion}
              onCancel={() => {
                setIsEditing(false);
                setEditQuestionData(null);
              }}
            />
          )}

          {/* Preview Table */}
          {!isEditing && uploadedData?.questions?.length > 0 && (
            <div
              style={{
                background: "white",
                padding: "20px",
                borderRadius: "8px",
                border: "1px solid #e0e0e0",
              }}
            >
              <h3
                style={{
                  fontSize: "16px",
                  fontWeight: "600",
                  color: "#2c3e50",
                  marginBottom: "15px",
                }}
              >
                Preview Uploaded Questions ({uploadedData.questions.length})
              </h3>
              <div style={{ overflowX: "auto" }}>
                <table className="upload-table">
                  <thead>
                    <tr>
                      <th style={{ width: "40px" }}>#</th>
                      <th>Question</th>
                      <th>Options</th>
                      <th style={{ width: "120px" }}>Type</th>
                      <th style={{ width: "100px" }}>Difficulty</th>
                      <th style={{ width: "150px" }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {uploadedData.questions.map((q, index) => (
                      <tr key={q.id}>
                        <td>{index + 1}</td>
                        <td>{q.question_text}</td>
                        <td>
                          {Array.isArray(q.options) && q.options.length > 0 ? (
                            <ol
                              type="A"
                              style={{ paddingLeft: "20px", margin: "0" }}
                            >
                              {q.options.map((opt, i) => (
                                <li key={i}>{opt.text}</li>
                              ))}
                            </ol>
                          ) : (
                            <span style={{ color: "#6c757d" }}>-</span>
                          )}
                        </td>
                        <td>{typeMap[q.question_typeid] || "Unknown"}</td>
                        <td>
                          {Object.keys(difficultyMapping).find(
                            (key) => difficultyMapping[key] === q.difficulty_id,
                          ) || "N/A"}
                        </td>
                        <td>
                          <div style={{ display: "flex", gap: "6px" }}>
                            <button
                              onClick={() => handleEditQuestion(q)}
                              style={{
                                background: "#f39c12",
                                color: "white",
                                border: "none",
                                padding: "6px 12px",
                                borderRadius: "5px",
                                fontWeight: "500",
                                cursor: "pointer",
                                fontSize: "12px",
                              }}
                            >
                              Edit
                            </button>
                            <button
                              onClick={() => handleDeleteQuestion(q.id)}
                              style={{
                                background: "#e74c3c",
                                color: "white",
                                border: "none",
                                padding: "6px 12px",
                                borderRadius: "5px",
                                fontWeight: "500",
                                cursor: "pointer",
                                fontSize: "12px",
                              }}
                            >
                              Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Upload Button */}
              <div style={{ marginTop: "20px", textAlign: "right" }}>
                {loadingUpload ? (
                  <p style={{ color: "#27ae60", fontWeight: "600" }}>
                    Uploading...
                  </p>
                ) : (
                  <button
                    onClick={handleUpload}
                    className="btn-upload"
                    disabled={
                      !uploadedData?.questions?.length ||
                      !selectedClass ||
                      !selectedSubject ||
                      !selectedChapter
                    }
                  >
                    Upload All Questions
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      </Popup>
    </>
  );
};

export default UploadQuestionsTab;
